import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import { t } from '@lingui/macro';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';

import useModalVisibility from 'hooks/useModalVisibility';
import useOpenBulkImport from 'hooks/useOpenBulkImport';

import { type SavedContextState, type SummaryData } from '../types';
import {
  validateBulkAssetFile,
  type BulkAssetFileError,
} from '../utils/validateBulkAssetFile';
import { parseBulkAssetCsv } from '../utils/parseBulkAssetCsv';
import { useBulkAddAssetsMutation } from '../hooks/useBulkAddAssetsMutation';
import { usePollAssetBulkUpload } from '../hooks/usePollAssetBulkUpload';

interface WizardModalHandles {
  stepOneProps?: {
    nextButtonProps: {
      onClick: () => void;
      disabled: boolean;
    };
  };
  wizardProps: {
    activeStep: number;
    onCancel: () => void;
    renderActions: (args: {
      isFirstStep: boolean;
      isLastStep: boolean;
      defaultActions: ReactNode;
    }) => ReactNode;
  };
  isProcessing: boolean;
  uploadedFile: File | null;
  setUploadedFile: (file: File | null) => void;
  // Validation result for the current file; `null` when the file is valid or
  // while validation is still running (see `stepOneProps.disabled`).
  fileError: BulkAssetFileError | null;
  savedState: SavedContextState;
  isModalOpen: boolean;
  openModal: () => void;
}

/* istanbul ignore next -- @preserve */
export const WizardContext = createContext<WizardModalHandles>(
  {} as WizardModalHandles,
);

interface BulkAddAssetProviderProps {
  children: ReactNode;
}

const EMPTY_SUMMARY: SummaryData = {
  reportUrl: undefined,
  assetsAddedCount: 0,
  assetsFailedCount: 0,
  depotsAddedCount: 0,
  depotsFailedCount: 0,
  stillProcessing: false,
};

const BulkAddAssetProvider = ({ children }: BulkAddAssetProviderProps) => {
  const { visible: isModalOpen, openModal, closeModal } = useModalVisibility();
  const [bulkAddAssets] = useBulkAddAssetsMutation();
  const { poll } = usePollAssetBulkUpload();

  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<{
    file: File;
    error: BulkAssetFileError | null;
  } | null>(null);
  const [savedState, setSavedState] = useState<SavedContextState>({
    summary: EMPTY_SUMMARY,
  });

  // Unlike the depot and contact wizards, this provider is mounted above the
  // modal's visibility gate and so outlives it — claim only while the modal is
  // actually up, otherwise the toast would stay suppressed forever.
  useOpenBulkImport(isModalOpen ? savedState.importId : undefined);

  const isModalOpenRef = useRef(isModalOpen);
  const isProcessingRef = useRef(isProcessing);
  const cancelledRef = useRef(false);

  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);
  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);
  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  useEffect(() => {
    if (!uploadedFile) return undefined;

    let cancelled = false;
    validateBulkAssetFile(uploadedFile).then((error) => {
      if (!cancelled) setValidation({ file: uploadedFile, error });
    });

    return () => {
      cancelled = true;
    };
  }, [uploadedFile]);

  const isValidated = !!uploadedFile && validation?.file === uploadedFile;
  const fileError = isValidated ? validation?.error ?? null : null;

  const resetSession = useCallback(() => {
    setCurrentStep(1);
    setIsProcessing(false);
    setUploadedFile(null);
    setValidation(null);
    setSavedState({ summary: EMPTY_SUMMARY });
  }, []);

  // Called once the poll reaches a terminal state. If the modal is still open
  // the user lands on the summary page. If it was closed, there is nothing to
  // show here — BulkImportToasts raises the toast off the completion event, so
  // this only tidies up for the next run.
  const handleResult = useCallback(
    (summary: SummaryData) => {
      if (cancelledRef.current) return;

      if (isModalOpenRef.current) {
        setSavedState((prev) => ({ ...prev, summary }));
        setIsProcessing(false);
      } else {
        resetSession();
      }
    },
    [resetSession],
  );

  const runBulkAdd = useCallback(
    async (file: File | null) => {
      const rows = file ? await parseBulkAssetCsv(file) : [];

      try {
        const { data } = await bulkAddAssets({
          variables: {
            assets: rows.map((row) => ({
              accountSourceId: row.accountSourceId,
              fmsId: row.depotFmsId,
              vin: row.vin,
              unitNumber: row.unitNumber,
              mileage: row.mileage,
              engineHours: row.engineHours,
            })),
          },
        });

        const uploadId = data?.bulkAddAssets?.assetBulkUpload?.id;
        setSavedState((prev) => ({ ...prev, importId: uploadId }));

        const enqueueFailures = data?.bulkAddAssets?.errors?.length ?? 0;
        const summary = await poll(uploadId, () => cancelledRef.current);

        handleResult({
          ...summary,
          assetsFailedCount: summary.assetsFailedCount + enqueueFailures,
        });
      } catch {
        handleResult({
          assetsAddedCount: 0,
          assetsFailedCount: rows.length,
          depotsAddedCount: 0,
          depotsFailedCount: 0,
          reportUrl: undefined,
          stillProcessing: false,
        });
      }
    },
    [bulkAddAssets, poll, handleResult],
  );

  const startProcessing = useCallback(() => {
    setIsProcessing(true);
    setCurrentStep(2);
    runBulkAdd(uploadedFile);
  }, [runBulkAdd, uploadedFile]);

  const handleClose = useCallback(() => {
    closeModal();
    if (!isProcessingRef.current) resetSession();
  }, [closeModal, resetSession]);

  const stepOneProps = useMemo(
    () => ({
      nextButtonProps: {
        onClick: startProcessing,
        disabled: !uploadedFile || !isValidated || !!fileError,
      },
    }),
    [startProcessing, uploadedFile, isValidated, fileError],
  );

  const wizardProps = useMemo(
    () => ({
      activeStep: currentStep === 2 && !isProcessing ? 3 : currentStep,
      onCancel: handleClose,
      renderActions: (args: {
        isFirstStep: boolean;
        isLastStep: boolean;
        defaultActions: ReactNode;
      }) =>
        args.isFirstStep ? (
          args.defaultActions
        ) : (
          <Flex flex={1} justifyContent="flex-end" style={{ gap: '8px' }}>
            {!args.isLastStep && (
              <Button
                text={t`Adding Assets`}
                loadingText={t`Adding Assets`}
                loading
                disabled
              />
            )}
            <Button
              text={t`Close`}
              onClick={handleClose}
              kind={args.isLastStep ? undefined : 'secondary'}
            />
          </Flex>
        ),
    }),
    [currentStep, handleClose, isProcessing],
  );

  const value = {
    stepOneProps,
    wizardProps,
    isProcessing,
    uploadedFile,
    setUploadedFile,
    fileError,
    savedState,
    isModalOpen,
    openModal,
  } satisfies WizardModalHandles;

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
};

export const useBulkAddAssetContext = () => useContext(WizardContext);

export default BulkAddAssetProvider;
